/** Reviewing one document: what was read, and whether a person accepts it.
 *
 * **Laid out as journey 2 describes it (2026-10-30): the document on the left, what was read from it
 * on the right.** The left pane (the text EmitIQ read, with the characters a field came from
 * highlighted) is sticky, so it stays in view while the reviewer works down the fields and the
 * choices beneath them; the fields, the confidence in each, the site, the use and the meter, then
 * accept or refuse, are one column on the right. That is a rearrangement and nothing else: every
 * question put to the reviewer, every refusal, `expectedVersion` and the gating of "Verify and
 * record" are as they were.
 *
 * This is the screen ADR-0008 and ADR-0011 both point at. Everything else in the product can be read
 * by anyone with a session; this is where a figure enters the inventory, and only a person can put it
 * there.
 *
 * Three decisions about what it shows.
 *
 * **Flagged fields first, and a missing field is a row.** The ordering comes from the API, which puts
 * low-confidence and missing fields at the top. A field the extractor did not find is rendered as an
 * explicit gap rather than being absent — an absent row is the easiest thing here to miss, and a
 * missing period is the error that produces a credible figure for the wrong year.
 *
 * **The facility is a required choice with no default.** One statement can cover several sites, so
 * extraction does not guess and neither does this form. A pre-selected first facility would be a
 * guess wearing a default's clothes, and it would be accepted by whoever was in a hurry.
 *
 * **Confidence is shown as a number, not a colour alone.** A colour says "look here"; the number is
 * what a reviewer can reason about, and it is what an assurance provider will ask about.
 */

import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useMe } from "../api/auth";
import { clearDraft, readDraft, reviewKey, writeDraft } from "../lib/drafts";
import {
  DuplicateActivity,
  useAddActivity,
  useCorrect,
  useDocument,
  useFacilities,
  useReject,
  useDocumentText,
  useReview,
  useUsage,
  useVerify,
} from "../api/evidence";
import type { Overlap, Rejected, Verified } from "../api/evidence";
import { isPeriodClosed } from "../api/refusal";
import { Loaded, ReadFailed, WriteFailed } from "../components/Loaded";
import { OutsideEngagement } from "../components/OutsideEngagement";
import { isEngaged } from "../lib/engagement";
import { Empty, Panel } from "../components/Panel";
import { InfoPopover } from "../components/ui";
import { StateChip } from "./StateChip";
import { ReviewConfirm } from "./processing/ReviewConfirm";
import { MeterPicker } from "./MeterPicker";
import { PostCloseRetry } from "./PeriodLock";
import { shortDate, statedQuantity, tonnes, unitLabel } from "../lib/format";
import styles from "./Evidence.module.css";
import { RequireSession } from "../shell/RequireSession";
import { FIELD_LABEL, drawn } from "./fields";
import { SourceTextPane } from "./SourceText";
import { ScreenFrame } from "./ScreenFrame";
import type { ScreenSearch } from "./search";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";

/* `FIELD_LABEL` moved to `SourceText.tsx` when the source pane needed the same map (ADR-0045).
 * Two screens with two copies of one piece of copy is how the second one comes to call a field
 * something the first does not, and a reviewer reading both at once sees the disagreement. */

function ReviewScreenBody() {
  const screen = useScreen();
  const navigate = useNavigate();
  const { documentId } = useParams({ from: "/evidence/$documentId" });
  const { data: review, isPending, error: reviewFailed, refetch: retryReview } = useReview(documentId);
  /** The site list is a **dependent read**, and an empty one is a claim (EXP-29). It defaulted to
   * `[]`, so a failed request rendered as a picker with nothing in it — *"this organisation has no
   * sites"* — on the one screen where choosing a site is the required act. A reviewer reads that
   * as having nowhere to put the figure and goes looking for an administrator. */
  const facilitiesRead = useFacilities();

  const [facilityId, setFacilityId] = useState("");
  /** The meter a person chose for this bill, from the site's registered meters (ADR-0098).
   *
   * Empty means *not said*, and that is the only default there is: nothing here is pre-selected,
   * inferred from the document, or carried over from another site. It is cleared whenever the site
   * or the fuel use changes, because the list it was chosen from has changed with them. */
  const [meterId, setMeterId] = useState("");
  /** Whether the reviewer ticked *remember* for the meter above (ADR-0107). Off unless ticked, and
   * only sent with a meter they chose. */
  const [rememberMeter, setRememberMeter] = useState(false);
  const [reason, setReason] = useState("");
  /** The refusal itself, not a sentence from it. `message` was a `string | null`, so a 403, a
   * `stale_version` 409 and a request that timed out at a proxy rendered identically — three
   * refusals with three different remedies, and the screen offered none of them. `WriteFailed`
   * reads the status and the code. */
  const [refusal, setRefusal] = useState<unknown>(null);
  const [outcome, setOutcome] = useState<Verified | null>(null);
  /** A rejection that **had already landed** — EXP-35. Held rather than navigated away from,
   * because the two are not the same event: on a repeat the state is terminal, the second
   * rejecter's wording was never recorded and cannot be, and the reason on the document is the
   * first rejecter's. Sending them back to the queue as though theirs were the rejection that
   * landed is the silent discard this requirement exists to stop. */
  const [rejected, setRejected] = useState<Rejected | null>(null);
  /** The overlap the server refused on, and the reviewer's answer to it (COL-05).
   *
   * Held in state rather than resolved inside the mutation, because the answer is a *decision* and a
   * decision needs a screen. `choice` is either the id of the figure this one replaces or the string
   * "separate" — deliberately not a boolean plus an optional id, which permits the two nonsensical
   * combinations and would need a runtime guard to exclude them.
   */
  const [overlaps, setOverlaps] = useState<readonly Overlap[] | null>(null);
  const [choice, setChoice] = useState<string>("");
  /** For a fuel invoice: was it burned in a vehicle or in fixed equipment?
   *
   * A generator's diesel and a van's diesel are different categories and different factors, and
   * most invoices do not say. There is deliberately **no default** (EXP-30) — the server refuses
   * without it, and a pre-selected "mobile" would be a guess wearing a default's clothes, accepted
   * by whoever was in a hurry. The same reasoning as the facility above.
   */
  const [fuelUse, setFuelUse] = useState<"" | "mobile" | "stationary">("");
  /** The field being corrected, and the two things a correction needs (F12).
   *
   * One at a time. A form that let a reviewer edit six fields and then save would make the reason
   * apply to all of them, and a reason that covers six changes explains none.
   */
  const [fixing, setFixing] = useState<string | null>(null);
  const [fixValue, setFixValue] = useState("");
  const [fixReason, setFixReason] = useState("");
  const correct = useCorrect(documentId);
  /** Which field's source position is highlighted in the pane beside this list (ADR-0045, DOC-03).
   *
   * **One piece of state, owned here**, because the selection appears in two places — this list's
   * "Show me where" and the pane's own list of what can be pointed at — and two copies is how a
   * screen comes to highlight one field while saying it is showing another. The pane is controlled
   * for that reason.
   */
  const [showing, setShowing] = useState<string | null>(null);
  /** The same query the pane makes, deduplicated by React Query's key.
   *
   * Read here only to decide whether a field **has** a position to offer. A "Show me where" button
   * on a field whose span is stale, displaced, corrected or never recorded is a control that
   * predictably does nothing (design rule 6) — and the sentence explaining why is in the pane,
   * from the server, rather than written twice.
   */
  const sourceText = useDocumentText(documentId, true);
  /** A statement covering more than one site, and this site's share of it (DOC-04).
   *
   * Behind a deliberate opt-in rather than always on screen. Most bills cover one site, and a
   * quantity box beside a figure the document already states invites somebody to retype it —
   * which is how a rounded number ends up in an inventory whose source says otherwise.
   */
  const [multiSite, setMultiSite] = useState(false);
  const [share, setShare] = useState("");
  /** Recording the next site, from the outcome screen. Same two answers, one at a time. */
  const [nextSite, setNextSite] = useState("");
  const [nextShare, setNextShare] = useState("");
  const [nextMeterId, setNextMeterId] = useState("");
  const addActivity = useAddActivity();

  /* **A half-finished review survives a closed tab** (EXP-35, ADR-0112). The identifiers and
   * choices of the decision - site, fuel use, meter, remember, multi-site and the share - are kept
   * in this browser under the person's own key, and put back **into the controls only**: nothing
   * is verified, rejected or recorded by restoring. The words a reviewer types (a rejection reason,
   * a correction) are never kept; see `lib/drafts.ts`. */
  const { data: me } = useMe();
  // **Why there is no review, for somebody whose reads a clamp narrows** (Q-03, G-Wauditor-04). The
  // review's 404 means "not read yet" for everyone, and for an engaged Auditor it also means "this
  // file is outside your engagement" - the server says the same thing for both, so this asks the
  // *document* (which an Auditor may read only when it is theirs): a document that comes back is
  // really waiting to be read, and one that does not is outside the engagement or not there.
  const engaged = isEngaged(me);
  const docRead = useDocument(documentId, engaged && !isPending && !reviewFailed && !review);
  const draftKey = me ? reviewKey(me, documentId) : null;
  const [draftReady, setDraftReady] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  //: **Restored during render, not in an effect** (React's "adjust state when an input changes"
  //: pattern): the controls then never paint empty and flip, and nothing cascades. It runs once per
  //: document, once the decision panel exists and the site list has arrived - a saved site is checked
  //: against the sites that exist before it is put back.
  if (draftKey !== null && draftReady !== draftKey && review && facilitiesRead.data) {
    setDraftReady(draftKey);
    if (review.may_verify) {
      const saved = readDraft(draftKey, "review");
      const site = facilitiesRead.data.some((facility) => facility.id === saved.facilityId)
        ? saved.facilityId
        : undefined;
      let any = false;
      if (site) {
        setFacilityId(site);
        any = true;
        if (saved.meterId) setMeterId(saved.meterId);
        if (saved.rememberMeter) setRememberMeter(true);
      }
      if (saved.fuelUse && review.document.kind === "fuel_invoice") {
        setFuelUse(saved.fuelUse);
        any = true;
      }
      if (saved.multiSite) {
        setMultiSite(true);
        if (saved.share) setShare(saved.share);
        any = true;
      }
      setRestored(any);
    }
    // No decision panel (a Viewer, or a document already decided): nothing is restored, and the
    // effect below, finding every control empty, removes whatever stale entry was there.
  }
  useEffect(() => {
    if (draftKey === null || draftReady !== draftKey || outcome !== null) return;
    writeDraft(draftKey, "review", {
      facilityId: facilityId || undefined,
      fuelUse: fuelUse || undefined,
      meterId: meterId || undefined,
      rememberMeter,
      multiSite,
      share: share || undefined,
    });
  }, [draftKey, draftReady, outcome, facilityId, fuelUse, meterId, rememberMeter, multiSite, share]);
  function discardDraft() {
    clearDraft(draftKey);
    setFacilityId("");
    setMeterId("");
    setRememberMeter(false);
    setFuelUse("");
    setMultiSite(false);
    setShare("");
    setRestored(false);
  }
  //: What this document became (EXP-19). Only once it is verified — before that it has produced
  //: nothing, and asking would be a request whose answer is always two empty lists.
  const { data: usage } = useUsage(documentId, review?.document.state === "verified");

  async function onCorrect() {
    if (!fixing) return;
    setRefusal(null);
    try {
      // **The expectation comes from the review on screen** (ADR-0054). `correction_id` is what
      // the server said was in force for this field when this reviewer read it, and `null` means
      // nothing was — which is an assertion the server checks, not a missing value. A second
      // reviewer who corrected the same field in the meantime makes this a 409 `stale_correction`
      // rather than a silent overwrite, and `setRefusal` below renders it.
      const item = review?.items.find((candidate) => candidate.name === fixing);
      await correct.mutateAsync({
        field: fixing,
        value: fixValue,
        reason: fixReason,
        expectedCorrectionId: item?.correction_id ?? null,
      });
      setFixing(null);
      setFixValue("");
      setFixReason("");
    } catch (error) {
      setRefusal(error);
    }
  }

  const verify = useVerify();
  const reject = useReject();
  const isFuelInvoice = review?.document.kind === "fuel_invoice";

  /** The answer the last attempt carried, so an Admin's retry after `period_closed` sends the
   * same request plus a reason rather than silently dropping a duplicate decision (ADR-0094). */
  const lastAnswer = useRef<string | undefined>(undefined);

  async function onVerify(answer?: string, postCloseReason?: string) {
    setRefusal(null);
    lastAnswer.current = answer;
    try {
      setOutcome(
        await verify.mutateAsync({
          documentId,
          facilityId,
          // **The version this reviewer read** (EXP-34). The route has refused a stale decision
          // since the backend half shipped, and no screen had ever sent one — so two reviewers
          // acting on the same document both succeeded, and a correction made while this panel was
          // open was accepted as though it had been reviewed. `document.version` is bumped by a
          // correction, and `useCorrect` writes the whole review back into the cache, so this is
          // the version on screen rather than the version when the page loaded.
          expectedVersion: review?.document.version,
          supersedeActivityRecordId:
            answer && answer !== "separate" ? answer : undefined,
          allowDuplicate: answer === "separate",
          fuelUse: fuelUse || undefined,
          quantity: multiSite && share ? share : undefined,
          meterId: meterId || undefined,
          rememberMeterReference: rememberMeter && meterId !== "",
          postCloseReason,
        }),
      );
      clearDraft(draftKey);
      setOverlaps(null);
    } catch (error) {
      if (error instanceof DuplicateActivity) {
        // Not a failure to report — a question to ask. The message goes into the panel below,
        // beside the figures it is about, rather than into the generic error line where it would
        // read as "something went wrong" and invite a retry of exactly the same request.
        setOverlaps(error.overlaps);
        setChoice("");
        return;
      }
      setRefusal(error);
    }
  }

  async function onAddSite(postCloseReason?: string) {
    setRefusal(null);
    try {
      setOutcome(
        await addActivity.mutateAsync({
          documentId,
          facilityId: nextSite,
          expectedVersion: review?.document.version,
          quantity: nextShare || undefined,
          fuelUse: fuelUse || undefined,
          meterId: nextMeterId || undefined,
          postCloseReason,
        }),
      );
      setNextSite("");
      setNextShare("");
      setNextMeterId("");
      clearDraft(draftKey);
    } catch (error) {
      // No duplicate branch here on purpose. Two sites on one bill do not overlap — the check is
      // on facility, category, carrier and period — so a collision at this point means something
      // the reviewer should read rather than a question with two buttons.
      setRefusal(error);
    }
  }

  async function onReject() {
    setRefusal(null);
    try {
      const answer = await reject.mutateAsync({
        documentId,
        reason,
        // A rejection decided against an extraction somebody has since corrected is a decision
        // about a document that no longer exists — and it is the direction nobody notices, because
        // a rejection applied by mistake looks like nothing having happened.
        expectedVersion: review?.document.version,
      });
      // **A repeat stops here instead of leaving.** The route answers `already_rejected` before the
      // state machine and before the staleness check — both of which would otherwise refuse a
      // retry of a request that worked — and the fact has to be said: their reason was not
      // recorded, and the one on the document is somebody else's.
      clearDraft(draftKey);
      if (answer.already_rejected) {
        setRejected(answer);
        return;
      }
      void navigate({ to: "/processing", search: { method: screen.method } });
    } catch (error) {
      setRefusal(error);
    }
  }

  // **A rejection that had already landed** (EXP-35). Before the outcome branch below, because a
  // repeat has to be announced rather than navigated away from — see `onReject`.
  if (rejected) {
    return (
      <ScreenFrame screen={screen} title="Already rejected" crumb="by an earlier request">
        <Panel title="This document was already rejected" column="1 / 8">
          <p className={styles.note} data-testid="already-rejected">
            <strong>Nothing was recorded by this request.</strong> The document was already
            rejected when it arrived — the ordinary reason is that an earlier attempt of yours
            reached EmitIQ and the response did not reach you. Rejection is terminal, so{" "}
            <strong>the reason you just typed was not recorded</strong>; the reason that stands is
            the one the first rejection carried.
          </p>
          {/* The reason that **was** recorded, which is the fact a second rejecter needs. Shown
              rather than described: it is what anybody reading this document later will see. */}
          <p className={styles.note} data-testid="recorded-rejection-reason">
            Recorded reason: {rejected.document.state_reason ?? "none was stored."}
          </p>
          <button
            className={styles.primary}
            type="button"
            onClick={() => void navigate({ to: "/processing", search: { method: screen.method } })}
          >
            Back to Processing
          </button>
        </Panel>
      </ScreenFrame>
    );
  }

  if (outcome) {
    return (
      <ScreenFrame screen={screen} title="Verified" crumb="the figure is in the inventory">
        <Panel title="Recorded" column="1 / 8">
          {/* **A retry that already succeeded is reassurance, not a second verification**
              (EXP-35). `calculations` and `ledger_entries` are **zero** on that path because this
              request wrote nothing — which is the point, and which rendered through the sentence
              below as *"a receipt: 0 calculations and 0 ledger entries"*: a verification that
              produced nothing, which is the one reading that sends somebody to do it again. The
              canonical case is a request that completed at the server and timed out at a proxy. */}
          {outcome.already_verified ? (
            <p className={styles.note} data-testid="already-verified">
              <strong>This document was already verified.</strong> The figure is in the inventory
              with its receipt, recorded by the earlier request — nothing was written a second
              time, and no total has changed. If your last attempt seemed to fail, it did not.
            </p>
          ) : (
          <p className={styles.note} data-testid="verified-recorded">
            The figure is in the inventory with a receipt: {outcome.calculations} calculation
            {outcome.calculations === 1 ? "" : "s"} and {outcome.ledger_entries} ledger entr
            {outcome.ledger_entries === 1 ? "y" : "ies"}. Dual reporting means an electricity figure
            produces two — location-based and market-based.
          </p>
          )}
          {/* A restatement is the outcome, not a footnote. The figure someone may already have
              quoted has changed, and the old receipt still resolves saying so (ADR-0030). */}
          {outcome.superseded_activity_record_id ? (
            <p className={styles.note}>
              It replaced an earlier figure, which is retained: its receipt still resolves and now
              says it was restated. Totals count this one only.
            </p>
          ) : null}
          {/* Why it became the category and carrier it did (INT-02). A classification nobody can
              see is one nobody can correct, and this one is derived from three separate facts. */}
          {outcome.because.length > 0 ? (
            <ul className={styles.reasons}>
              {outcome.because.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          {outcome.accepted_alongside.length > 0 ? (
            <p className={styles.note}>
              Recorded alongside {outcome.accepted_alongside.length} existing figure
              {outcome.accepted_alongside.length === 1 ? "" : "s"} for the same site and period,
              because you said it is a separate consumption. Both count.
            </p>
          ) : null}
          {/* What the statement evidences and nobody has claimed (DOC-04). Said here because this
              is the moment it is actionable, and because a positive number is an **understatement**
              waiting to happen: consumption the document proves and no total contains. */}
          {outcome.unallocated && Number(outcome.unallocated) > 0 ? (
            <div className={styles.duplicate} role="group" aria-label="More of this statement">
              <p className={styles.duplicateHead}>
                <span className="num">{outcome.unallocated}</span> of this statement is not yet
                attributed to a site.
              </p>
              <p className={styles.note}>
                It is on the bill and in no total. Record the site it belongs to, or leave it — but
                leaving it means the inventory is short by that much.
              </p>
              <Loaded
                query={facilitiesRead}
                of="The list of sites"
                empty="No other site is set up to attribute the rest to."
              >
                {(sites) => (
                  <label className={styles.field}>
                    <span>Which other site?</span>
                    <select
                      aria-label="Which other site?"
                      value={nextSite}
                      onChange={(event) => {
                        setNextSite(event.target.value);
                        setNextMeterId("");
                      }}
                    >
                      <option value="">Choose a site…</option>
                      {sites
                        .filter((facility) => facility.id !== facilityId)
                        .map((facility) => (
                          <option key={facility.id} value={facility.id}>
                            {facility.name}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
              </Loaded>
              <MeterPicker
                documentId={documentId}
                facilityId={nextSite}
                fuelUse={fuelUse || undefined}
                value={nextMeterId}
                onChange={setNextMeterId}
                name="Which meter was this site's share for?"
              />
              <label className={styles.field}>
                <span>How much of it?</span>
                <input
                  inputMode="decimal"
                  aria-label="How much of it?"
                  value={nextShare}
                  onChange={(event) => setNextShare(event.target.value)}
                  placeholder={String(outcome.unallocated)}
                />
              </label>
              <button
                className={styles.primary}
                type="button"
                disabled={!nextSite || addActivity.isPending}
                onClick={() => void onAddSite()}
              >
                {addActivity.isPending ? "Recording…" : "Record this site too"}
              </button>
            </div>
          ) : null}
          {/* Another site's share is a second write into the inventory, so it can reach a period
              that closed after the first (ADR-0094). The retry repeats **this** write. */}
          <PostCloseRetry
            error={refusal}
            pending={addActivity.isPending}
            onRecord={(reason) => void onAddSite(reason)}
          />
          <WriteFailed
            error={isPeriodClosed(refusal) ? null : refusal}
            of="this statement"
            onReload={() => void retryReview()}
          />
          <button
            className={styles.primary}
            type="button"
            onClick={() => void navigate({ to: "/emissions", search: { method: screen.method } })}
          >
            See it on Emissions
          </button>
          {/* **The way back to the queue** (journey walk, 2026-10-31). A reviewer with a shelf of
              documents recorded the first and was left with one control, which led away from the
              rest. The rejected and the Contributor hand-off panels already offer this. */}{" "}
          <button
            className={styles.ghost}
            type="button"
            data-testid="recorded-back-to-evidence"
            onClick={() => void navigate({ to: "/processing", search: { method: screen.method } })}
          >
            Back to Processing
          </button>
        </Panel>
      </ScreenFrame>
    );
  }

  if (isPending) {
    return (
      <ScreenFrame screen={screen} title="Review" crumb="loading">
        {null}
      </ScreenFrame>
    );
  }

  // **"Not read yet" and "the read failed" were one branch** (EXP-29), and they are opposite
  // instructions: the first says extract the document, the second says try again. A reviewer told
  // the first when the second is true goes and re-extracts a document that was fine.
  if (reviewFailed) {
    return (
      <ScreenFrame screen={screen} title="Review" crumb="could not be read">
        <Panel title="Review" column="1 / 8">
          <ReadFailed
            of="This document's extraction"
            error={reviewFailed}
            onRetry={() => void retryReview()}
          />
        </Panel>
      </ScreenFrame>
    );
  }

  if (!review && engaged && docRead.isPending) {
    return (
      <ScreenFrame screen={screen} title="Review" crumb="loading">
        {null}
      </ScreenFrame>
    );
  }

  if (!review && engaged && docRead.error) {
    return (
      <ScreenFrame screen={screen} title="Review" crumb="could not be read">
        <Panel title="Review" column="1 / 8">
          <ReadFailed
            of="This document"
            error={docRead.error}
            onRetry={() => void docRead.refetch()}
          />
        </Panel>
      </ScreenFrame>
    );
  }

  if (!review && engaged && docRead.data === null) {
    return (
      <ScreenFrame screen={screen} title="Review" crumb="not available">
        <Panel title="Not available" column="1 / 8">
          <OutsideEngagement noun="document" />
        </Panel>
      </ScreenFrame>
    );
  }

  if (!review) {
    return (
      <ScreenFrame screen={screen} title="Review">
        <Panel title="Nothing to review" column="1 / 8">
          <Empty>This document has not been read yet.</Empty>
        </Panel>
      </ScreenFrame>
    );
  }

  const gaps = review.items.filter((item) => item.missing);
  const flagged = review.items.filter((item) => item.low_confidence && !item.missing);

  return (
    <ScreenFrame
      screen={screen}
      title="Review"
      crumb={review.document.original_filename ?? "document"}
      /* **This screen has four panel rows and the shell's grid defines two.**
         `AppShell.module.css`'s body is `grid-template-rows: auto minmax(0, 1fr)` with
         `overflow: hidden`, so anything past the second row is clipped rather than revealed —
         and on 2026-10-11 that clipped the **"Accept it"** panel down to about fifteen pixels,
         taking the *"Verify and record"* button with it. The button was still in the DOM, still
         enabled, and `overflow: hidden` on `.panel` meant it could not be seen or clicked: a
         reviewer could read a document and had no way to accept it.

         It surfaced when the redaction line below grew — the old copy claimed redaction had run
         and the honest replacement is three sentences — so a correct change to one panel silently
         removed the primary action from another. That is the hazard of a fixed-height grid, and
         the eight other screens with more than two panel rows already pass this flag. */
      bodyScrolls
    >
      {/* Which document this is, as one strip, before anything is asked about it. Facts the review
          already holds - nothing here is looked up. */}
      <div className={styles.docStrip} data-testid="review-document">
        <div className={styles.stripItem}>
          <span className={styles.stripLabel}>Document</span>
          <span className={styles.stripValue} title={review.document.original_filename ?? undefined}>
            {review.document.original_filename ?? "Untitled document"}
          </span>
        </div>
        <div className={styles.stripItem}>
          <span className={styles.stripLabel}>Kind</span>
          <span className={styles.stripValue}>{review.document.kind.replace(/_/g, " ")}</span>
        </div>
        <div className={styles.stripItem}>
          <span className={styles.stripLabel}>Period</span>
          <span className={styles.stripValue}>
            {review.document.period_start && review.document.period_end
              ? `${shortDate(review.document.period_start)} \u2013 ${shortDate(review.document.period_end)}`
              : "not established"}
          </span>
        </div>
        <div className={styles.stripItem}>
          <span className={styles.stripLabel}>State</span>
          <StateChip state={review.document.state} />
        </div>
      </div>

      {/* **The document, on the left** (ADR-0045 option B, DOC-03's unmet clause). The text the
          extractor read, with the characters a field was read from highlighted when the server has
          re-checked them - and a named reason when it has not. It is a reader: the correction is
          still made by the control in the list beside it, which appends, requires a reason and is
          merged once in promotion. A second write path from here is how two screens come to
          disagree about what a correction is. */}
      <div className={styles.reviewDoc}>
        <SourceTextPane
          documentId={documentId}
          selected={showing}
          onSelect={setShowing}
          column="auto"
        />
      </div>

      <Panel
        title="What was read"
        sub={
          gaps.length || flagged.length
            ? `${gaps.length + flagged.length} need${gaps.length + flagged.length === 1 ? "s" : ""} attention`
            : "nothing flagged"
        }
        /* Six columns: the source pane sits in the other six, **beside** this list rather than
           under it. That adjacency is the requirement — DOC-03 is a correction made *while
           viewing its source*, and a reviewer who has to leave the screen to look at the document
           is the error the feature exists to prevent. */
        column="7 / 13"
        flush
      >
        {/* DOC-07, at the top because a reviewer reads the heading before the rows. The extractor
            is allowed to read the first of several sections; what it may not do is present the
            result as though the document said one thing. */}
        {review.partially_read ? (
          <p className={styles.caveat}>
            <span className={styles.caveatTitle}>Read in part</span>
            {review.partially_read_because}
          </p>
        ) : null}
        <div className={styles.fieldsHead} aria-hidden="true">
          <span>Field</span>
          <span>Read as</span>
          <span>Confidence</span>
          <span />
        </div>
        <ul className={styles.fields}>
          {review.items.map((item) => (
            <li
              key={item.name}
              className={`${styles.field_row} ${item.missing ? styles.gap : ""} ${
                item.low_confidence && !item.missing ? styles.flagged : ""
              }`}
            >
              <span className={styles.fieldName}>{FIELD_LABEL[item.name] ?? item.name}</span>
              <span className={styles.fieldValue}>
                {item.missing ? (
                  <em>not found on the document</em>
                ) : (
                  <>
                    {item.value}
                    {item.unit ? <span className={styles.unit}> {unitLabel(item.unit)}</span> : null}
                  </>
                )}
              </span>
              <span className={styles.confidence}>
                {item.corrected
                  ? "fixed"
                  : item.missing
                    ? "—"
                    : `${Math.round(Number(item.confidence) * 100)}%`}
              </span>

              {/* The document says this more than once and one of them was read (DOC-07). Beside
                  the value, because the value is what looks complete — a fifty-page bill read as
                  one line is not a wrong figure, it is a right figure standing in for eleven that
                  are missing. */}
              {item.candidates > 1 ? (
                <span className={styles.corrected}>
                  This document states it {item.candidates} times. The first was read — check
                  whether the others are the same figure or separate periods.
                </span>
              ) : null}

              {/* What a person changed, and why — beside the value, not in a log. A corrected
                  figure whose reason lives somewhere else is a figure nobody can defend at the
                  moment they are asked to. */}
              {item.corrected ? (
                <span className={styles.corrected}>
                  {item.extracted_value
                    ? `was ${item.extracted_value} · `
                    : "extraction found nothing here · "}
                  {item.correction_reason}
                </span>
              ) : null}

              {review.may_correct && fixing !== item.name ? (
                <button
                  type="button"
                  className={styles.fixLink}
                  onClick={() => {
                    setFixing(item.name);
                    setFixValue(item.missing ? "" : item.value);
                    setFixReason("");
                    setRefusal(null);
                  }}
                >
                  {item.missing ? "Fill it in" : "Fix it"}
                </button>
              ) : null}

              {/* **ADR-0045, and DOC-03's remaining clause**: the correction above is made
                  *while viewing its source*, so the characters this value was read from are one
                  click away in the pane beside this list — not a tab, not a second screen.
                  Offered only when there is a position the server has re-checked; a field whose
                  span is stale, displaced, corrected or never recorded has nothing to show, and
                  the pane says which of those it is rather than this row offering a button that
                  does nothing. */}
              {drawn(sourceText.data?.spans.find((span) => span.field === item.name)) ? (
                <button
                  type="button"
                  className={styles.fixLink}
                  aria-label={`Show where ${FIELD_LABEL[item.name] ?? item.name} was read from`}
                  onClick={() => setShowing(showing === item.name ? null : item.name)}
                >
                  {showing === item.name ? "Hide the source" : "Show me where"}
                </button>
              ) : null}

              {fixing === item.name ? (
                <div className={styles.fixForm}>
                  <input
                    className={styles.fixInput}
                    aria-label={`Corrected value for ${FIELD_LABEL[item.name] ?? item.name}`}
                    value={fixValue}
                    onChange={(event) => setFixValue(event.target.value)}
                    placeholder={item.unit ? `value in ${unitLabel(item.unit)}` : "value"}
                  />
                  <input
                    className={styles.fixInput}
                    aria-label={`Why are you changing ${FIELD_LABEL[item.name] ?? item.name}?`}
                    value={fixReason}
                    onChange={(event) => setFixReason(event.target.value)}
                    placeholder="why — e.g. read from the statement header"
                  />
                  <div className={styles.fixButtons}>
                    <button
                      type="button"
                      className={styles.primary}
                      disabled={!fixValue.trim() || !fixReason.trim() || correct.isPending}
                      onClick={() => void onCorrect()}
                    >
                      {correct.isPending ? "Saving…" : "Save"}
                    </button>
                    {/* Cancel **restores the prior state**, which means both fields and the
                        refusal as well as the panel: closing it with a half-typed value and a
                        stale refusal still in state means the next field opened shows somebody
                        else's correction and a sentence about a request they did not make. */}
                    <button
                      type="button"
                      className={styles.ghost}
                      onClick={() => {
                        setFixing(null);
                        setFixValue("");
                        setFixReason("");
                        setRefusal(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {gaps.length ? (
          <p className={`${styles.note} ${styles.pad}`}>
            A required field was not found. Nothing can be recorded from this document until it is —
            a quantity with no period cannot be placed in a reporting year.
          </p>
        ) : null}
      </Panel>

      {/* **Role-aware** (F12). This panel rendered a verify button for everyone, so a Contributor's
          only way to learn they could not accept a figure was to press it and collect a 403. The
          server says what this person may do; the browser does not infer it. */}
      <Panel
        title={review.may_verify ? "Accept it" : "What happens next"}
        sub={review.may_verify ? "Only a person can" : "Your part is done"}
        column="7 / 13"
      >
        {review.may_verify ? null : (
          <>
            <ReviewConfirm
              document={review.document}
              facilityId=""
              fuelUse=""
              mayClassify={false}
              mayVendor={review.may_correct}
              mayAddVendor={false}
            />
            <p className={styles.note}>{review.handoff}</p>
            <button
              className={styles.ghost}
              type="button"
              onClick={() => void navigate({ to: "/processing", search: { method: screen.method } })}
            >
              Back to Processing
            </button>
          </>
        )}
        {review.may_verify ? (
        <>
        {restored ? (
          <p className={styles.note} role="status" data-testid="draft-restored">
            <strong>Your earlier choices on this document were put back.</strong> Nothing has been
            recorded; check them, then decide. Only choices are kept, never anything you typed.{" "}
            <button
              type="button"
              className={styles.ghost}
              data-testid="draft-discard"
              onClick={discardDraft}
            >
              Start again
            </button>
          </p>
        ) : null}
        <Loaded
          query={facilitiesRead}
          of="The list of sites"
          empty="No sites are set up yet. Add one on Organisation before recording a figure against it."
        >
          {(sites) => (
            <label className={styles.field}>
              <span>Which site does this cover?</span>
              <select
                aria-label="Which site does this cover?"
                value={facilityId}
                onChange={(event) => {
                  setFacilityId(event.target.value);
                  setMeterId("");
                }}
              >
                <option value="">Choose a site…</option>
                {sites.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </Loaded>
        <InfoPopover infoKey="processing.review-site" />
        <ReviewConfirm
          document={review.document}
          facilityId={facilityId}
          fuelUse={fuelUse}
          mayClassify
          mayVendor={review.may_correct}
          mayAddVendor={me?.role === "admin" || me?.role === "approver"}
        />

        {/* DOC-04. The sentence above has always been true and the product had no way to act on
            it: recording a second site superseded the first, so half a two-site bill silently
            became the whole of it. Behind an opt-in because most bills cover one site — and a
            quantity box beside a figure the document already states invites somebody to retype
            it, which is how a rounded number gets into an inventory whose source says otherwise. */}
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={multiSite}
            onChange={(event) => {
              setMultiSite(event.target.checked);
              if (!event.target.checked) setShare("");
            }}
          />
          <span>This statement covers more than one site</span>
        </label>
        {multiSite ? (
          <>
            <label className={styles.field}>
              <span>How much of it belongs to this site?</span>
              <input
                inputMode="decimal"
                aria-label="How much of it belongs to this site?"
                value={share}
                onChange={(event) => setShare(event.target.value)}
                placeholder="the quantity for this site alone"
              />
            </label>
            <p className={styles.note}>
              The statement&rsquo;s own figure is not changed — it says what it says. Record each
              site&rsquo;s share and the rest stays outstanding until somebody accounts for it.
            </p>
          </>
        ) : null}

        {/* Only for a fuel invoice, and only because the document cannot answer it. A generator's
            diesel is Scope 1 stationary combustion and a van's is mobile — different categories,
            different factors — and the invoice says how much was delivered, not what burned it. */}
        {isFuelInvoice ? (
          <>
            <label className={styles.field}>
              <span>How was this fuel used?</span>
              <select
                aria-label="How was this fuel used?"
                value={fuelUse}
                onChange={(event) => {
                  setFuelUse(event.target.value as "" | "mobile" | "stationary");
                  setMeterId("");
                }}
              >
                <option value="">Choose…</option>
                <option value="mobile">In a vehicle</option>
                <option value="stationary">In fixed equipment — a generator, a boiler</option>
              </select>
            </label>
            <p className={styles.note}>
              The invoice says how much fuel was delivered, not what burned it. These are different
              categories with different factors, so there is no default.
            </p>
          </>
        ) : null}

        {/* ADR-0098. Offered only where the chosen site has a stream declared for one meter, and
            never pre-selected: a person says which supply this was, the bill's own reference is
            not looked up (SEC-05). Renders nothing for every other site. */}
        <MeterPicker
          documentId={documentId}
          facilityId={facilityId}
          fuelUse={fuelUse || undefined}
          value={meterId}
          onChange={setMeterId}
          name="Which meter was this bill for?"
          remember={rememberMeter}
          onRememberChange={setRememberMeter}
        />
        {overlaps ? null : (
          <>
          <InfoPopover infoKey="processing.review-accept" />
          <button
            className={styles.primary}
            type="button"
              disabled={
              !facilityId || (isFuelInvoice && !fuelUse) || gaps.length > 0 || verify.isPending
            }
            onClick={() => void onVerify()}
          >
            {verify.isPending ? "Recording…" : "Verify and record"}
          </button>
          </>
        )}

        {/* The reviewable replacement decision (EXP-31, COL-05). Nothing has been written: the
            request was refused whole, so the document is exactly as it was and this is a question,
            not a recovery. There is no default — a pre-selected "replace the first one" would be a
            guess about the customer's meters, and choosing wrongly either loses a real consumption
            or doubles one. */}
        {overlaps ? (
          <div className={styles.duplicate} role="group" aria-label="This overlaps a figure already recorded">
            <p className={styles.duplicateHead}>
              This already has a figure for that meter and period.
            </p>
            <p className={styles.note}>
              Recording it as another one would overstate the total. Say which this is — nothing has
              been saved yet.
            </p>
            {overlaps.map((overlap) => (
              <label key={overlap.activity_record_id} className={styles.choice}>
                <input
                  type="radio"
                  name="duplicate-choice"
                  value={overlap.activity_record_id}
                  checked={choice === overlap.activity_record_id}
                  onChange={(event) => setChoice(event.target.value)}
                />
                <span>
                  It corrects{" "}
                  {/* `statedQuantity`, not the raw string: a stored NUMERIC reached this line as
                      `301500.000000 kWh` (journey walk, 2026-10-31) - rule 5 puts presentation in
                      the presentation layer. */}
                  <span className="num">{statedQuantity(overlap.quantity)}</span> {unitLabel(overlap.unit)} for{" "}
                  {shortDate(overlap.period_start)} – {shortDate(overlap.period_end)}
                  {overlap.method === "estimated" ? " (an estimate)" : ""}
                  {/* Which file, and which supplier (DOC-05). Two vendors number their invoices
                      the same way often enough that a quantity and a date cannot tell them apart
                      — and this is the question where telling them apart is the whole task. */}
                  {overlap.source_supplier || overlap.source_filename ? (
                    <span className={styles.choiceSource}>
                      {[overlap.source_supplier, overlap.source_filename]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
            <label className={styles.choice}>
              <input
                type="radio"
                name="duplicate-choice"
                value="separate"
                checked={choice === "separate"}
                onChange={(event) => setChoice(event.target.value)}
              />
              <span>
                It is a separate consumption — a different meter at the same site, so both count
              </span>
            </label>
            <button
              className={styles.primary}
              type="button"
              disabled={!choice || verify.isPending}
              onClick={() => void onVerify(choice)}
            >
              {verify.isPending ? "Recording…" : "Record with that decision"}
            </button>
          </div>
        ) : null}

        <hr className={styles.rule} />

        <label className={styles.field}>
          <span>Or refuse it, and say why</span>
          <input
            aria-label="Why are you refusing it?"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. covers a site outside the boundary"
          />
        </label>
        <div className={styles.fixButtons}>
          <button
            className={styles.ghost}
            type="button"
            disabled={!reason.trim() || reject.isPending}
            onClick={() => void onReject()}
          >
            Reject
          </button>
          {/* Cancel has meaning the moment there is something to cancel, and not before: a
              permanent "Cancel" beside an empty field is a control that does nothing. */}
          {reason ? (
            <button
              className={styles.fixLink}
              type="button"
              onClick={() => {
                setReason("");
                setRefusal(null);
              }}
            >
              Cancel
            </button>
          ) : null}
        </div>
        </>
        ) : null}
      </Panel>

      {/* EXP-19, the direction that did not exist: a document knew nothing about what it
          produced, so *"and where does this bill end up?"* — the question an assurance provider
          asks second — had no answer in the product. */}
      {usage && usage.activities.length > 0 ? (
        <Panel
          title="What this became"
          sub={`${usage.activities.length} activity record${usage.activities.length === 1 ? "" : "s"}`}
          column="1 / 13"
        >
          {/* **No `className`, because `styles.rows` was never written.** A bare reference to a
              class a stylesheet does not declare puts the literal string `"undefined"` into the
              `class` attribute — `vite-env.d.ts` types a CSS module as
              `Readonly<Record<string, string>>` on purpose, so a missing key compiles. Nothing is
              styled either way; what changes is that the DOM stops asserting a class that was
              never written. Found 2026-10-11 by `test_no_css_module_reference_dangles`, which
              sweeps every `module.css` reference against its own stylesheet. */}
          <div>
            {usage.activities.map((activity) => (
              <div key={activity.activity_record_id} className={styles.usageRow}>
                <span className={styles.usageMain}>
                  {activity.facility_name}
                  <span className={styles.usageSub}>
                    {statedQuantity(activity.quantity)} {unitLabel(activity.unit)} ·{" "}
                    {shortDate(activity.period_start)} – {shortDate(activity.period_end)}
                    {/* A superseded record is listed and says so. Dropping it would make a
                        corrected bill look as though it had always been right. */}
                    {activity.is_effective ? "" : " · restated, no longer counted"}
                  </span>
                </span>
                <span className={styles.usageFigures}>
                  {activity.calculations.length === 0 ? (
                    <span className={styles.note}>no figure</span>
                  ) : (
                    activity.calculations.map((calculation) => (
                      <Link
                        key={calculation.calculation_id}
                        className={styles.fixLink}
                        to="/emissions"
                        // The receipt is the point of this link; the basis and the window are
                        // carried rather than reset to `?method=location`, which would open a
                        // location-based receipt for somebody reading the market-based inventory.
                        search={(prev: Partial<ScreenSearch>) => ({
                          ...keepSearch(prev),
                          receipt: calculation.calculation_id,
                        })}
                      >
                        Scope {calculation.scope} · {tonnes(calculation.kgco2e, 2)} tCO₂e
                      </Link>
                    ))
                  )}
                </span>
              </div>
            ))}
          </div>

          {/* **The sentence is the server's, and it had to be: this screen's own was false.**
              It read *"Nothing has been filed — EmitIQ does not yet produce a filing artifact"*,
              citing ADR-0020 as `Proposed`. ADR-0020 was accepted on 2026-09-29 and built the
              same day — `regimes/artifact.py` issues a filing, hashes it into the chain and
              re-derives it, and the Reports screen shows "Filings issued" — so this told a
              customer the product cannot do something it does, which is the understating
              direction of the same defect as claiming it can.

              `FilingUse.note` exists for exactly this. Its own comment says the distinction is
              *"the product's and not the screen's"* — written on the server so a screen would not
              invent one. This screen invented one anyway, which is what that comment was trying
              to prevent, so the fix is to render what the server says rather than to reword. */}
          {usage.filings.length > 0 ? (
            <p className={styles.note} style={{ marginTop: 10 }}>
              These figures fall inside{" "}
              {usage.filings
                .map((f) => `${f.regime_key.replace(/_/g, " ")} (${f.legal_entity_name})`)
                .join(", ")}
              . {usage.filings[0]?.note}
            </p>
          ) : (
            <p className={styles.note} style={{ marginTop: 10 }}>
              No reporting period covers these dates, so nothing here would be stated in a report
              yet.
            </p>
          )}
        </Panel>
      ) : null}

      <Panel title="How it was read" column="1 / 13">
        <dl className={styles.provenance}>
          <div>
            <dt>Read by</dt>
            <dd>
              {review.extraction.provider} · {review.extraction.model}
            </dd>
          </div>
          <div>
            <dt>Prompt / schema</dt>
            <dd>
              {review.extraction.prompt_version} / {review.extraction.schema_version}
            </dd>
          </div>
          {/* **`null` is not an affirmative answer, and this said it was.** The dd read
              *"yes, before reading"* for a null `redacted_items` — and on this screen it is
              **always** null, because `ExtractionOut.of` passes `removed=None` when it is reading
              an existing row rather than reporting a run. So the one line in the product that
              spoke for ADR-0011's redaction gate asserted the gate had held, from a payload that
              says nothing about it, on every document. The three answers are different sentences
              (EXP-35): a count, a count of nothing, and no count at all. `data-asked` is the
              structural form, because the copy has to name redaction in order to say it is not
              reporting on it. */}
          <div>
            <dt>Names removed first</dt>
            <dd
              data-testid="redaction-count"
              data-asked={review.extraction.redacted_items == null ? "no" : "yes"}
            >
              {review.extraction.redacted_items == null
                ? "not reported on this read — the count belongs to the reading, and this is the record it produced"
                : review.extraction.redacted_items === 0
                  ? "none found — redaction ran and removed nothing"
                  : `${review.extraction.redacted_items} item(s) removed`}
            </dd>
          </div>
          <div>
            <dt>Retain until</dt>
            <dd>
              {review.document.delete_after ? shortDate(review.document.delete_after) : "—"}
            </dd>
          </div>
        </dl>
        <InfoPopover infoKey="processing.how-read" />
      </Panel>

      {/* **The refusal, in the words its own status earns.** Unconditional rather than guarded:
          `WriteFailed` renders nothing for no error, and a guard is where the next person forgets
          a branch. `onReload` re-reads the extraction, which is the remedy for a stale decision
          and for a request whose outcome is unknown — in both cases this screen's copy of the
          document is the thing that is wrong. */}
      {/* A verify into a closed period (ADR-0094) is its own refusal, with an Admin's way through. */}
      <PostCloseRetry
        error={refusal}
        pending={verify.isPending}
        onRecord={(reason) => void onVerify(lastAnswer.current, reason)}
      />
      <WriteFailed
        error={isPeriodClosed(refusal) ? null : refusal}
        of="this document"
        onReload={() => void retryReview()}
      />
    </ScreenFrame>
  );
}


/** Wrapped, so an unauthenticated visitor gets the sign-in form rather than a screen whose every
 * query 401s. The wrapper decides what to show; the API decides what may be read. */
export function ReviewScreen() {
  return (
    <RequireSession>
      <ReviewScreenBody />
    </RequireSession>
  );
}
